import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Copy,
  Snowflake,
  UserSearch,
  Wallet,
} from "lucide-react";
import duckMoneyGif from "../../../assets/utya-money.gif";
import duckPremiumStarsGif from "../../../assets/utya-premium-stars.gif";
import duckLookingGif from "../../../assets/utya-looking.gif";
import { FINANCE_STATUS_LABELS, labelFrom } from "../../../lib/labels";
import {
  getTelegramUser,
  openTelegramLink,
  triggerHapticFeedback,
} from "../../../lib/tg";
import { useVisibleRefresh } from "../../../lib/useVisibleRefresh";
import { driverApi } from "../lib/api";

type DriverBalance = {
  available: string;
  frozen: string;
  withdrawal_pending: string;
};

type DriverApiKey = {
  api_key: string;
  docs_url: string;
};

type WithdrawalMethodValue = "crypto_check" | "support";

type WithdrawalRate = {
  rate_rub_usdt: string;
  fetched_at: string;
  expires_at?: string;
  source: string;
  is_fallback?: boolean;
};

type WithdrawalRequestSummary = {
  amountRub: string;
  amountUsdt: string;
};

type FinanceOperation = {
  id: string;
  kind?: string;
  title: string;
  amount_rub: string;
  amount_usdt?: string | null;
  status: string;
  method?: string | null;
  created_at: string;
  details?: string | null;
};

type Props = {
  onBack: () => void;
};

const WITHDRAWAL_RATE_TTL_MS = 15 * 60 * 1000;
const MIN_WITHDRAWAL_RUB = 100;
const MANAGER_LINK = "https://t.me/m/zuEpyK4gOTY6";
const MANAGER_DESTINATION = "manager:@Pinkpko";

export function DriverBalance({ onBack: _onBack }: Props) {
  const [balance, setBalance] = useState<DriverBalance | null>(null);
  const [operations, setOperations] = useState<FinanceOperation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [withdrawMode, setWithdrawMode] = useState(false);
  const [historyMode, setHistoryMode] = useState(false);
  const [withdrawMethod, setWithdrawMethod] =
    useState<WithdrawalMethodValue | null>(null);
  const [amount, setAmount] = useState("");
  const [usdtAmount, setUsdtAmount] = useState("");
  const [conversionSource, setConversionSource] = useState<"rub" | "usdt">(
    "rub",
  );
  const [withdrawalRate, setWithdrawalRate] = useState<WithdrawalRate | null>(
    null,
  );
  const [rateLoading, setRateLoading] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [managerRequest, setManagerRequest] =
    useState<WithdrawalRequestSummary | null>(null);
  const [submitting, setSubmitting] = useState<WithdrawalMethodValue | null>(
    null,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmingCancellationId, setConfirmingCancellationId] = useState<
    number | null
  >(null);
  const [cancellingWithdrawalId, setCancellingWithdrawalId] = useState<
    number | null
  >(null);
  const [cancellationError, setCancellationError] = useState<string | null>(
    null,
  );
  const mountedRef = useRef(false);
  const balanceRequestRef = useRef<ReturnType<
    typeof driverApi.getBalance
  > | null>(null);

  const requestBalance = useCallback(async () => {
    if (balanceRequestRef.current) return await balanceRequestRef.current;
    const request = driverApi.getBalance();
    balanceRequestRef.current = request;
    try {
      return await request;
    } finally {
      if (balanceRequestRef.current === request) {
        balanceRequestRef.current = null;
      }
    }
  }, []);

  const refreshBalance = useCallback(
    async ({ reportError = false }: { reportError?: boolean } = {}) => {
      try {
        const response = await requestBalance();
        if (!mountedRef.current) return false;
        if (response.error || !response.data) {
          if (reportError) setError("не удалось загрузить баланс");
          return false;
        }
        setBalance(response.data as DriverBalance);
        setError(null);
        return true;
      } catch {
        if (mountedRef.current && reportError) {
          setError("не удалось загрузить баланс");
        }
        return false;
      }
    },
    [requestBalance],
  );

  const refreshOperations = useCallback(async () => {
    try {
      const response = await driverApi.listOperations();
      if (!mountedRef.current || response.error || !response.data) return false;
      setOperations(response.data as FinanceOperation[]);
      return true;
    } catch {
      return false;
    }
  }, []);

  const refreshFinanceData = useCallback(async () => {
    const [balanceUpdated] = await Promise.all([
      refreshBalance(),
      refreshOperations(),
    ]);
    if (!balanceUpdated) throw new Error("balance_load_failed");
  }, [refreshBalance, refreshOperations]);

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoading(true);
    setError(null);
    void refreshOperations();
    await refreshBalance({ reportError: true });
    if (mountedRef.current) setLoading(false);
  }, [refreshBalance, refreshOperations]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  useVisibleRefresh(refreshBalance);

  useEffect(() => {
    const rate = rateRub();
    if (!withdrawMethod || rate === null) return;
    if (conversionSource === "rub") {
      setUsdtAmount(calculateUsdtAmount(parseAmountInput(amount), rate));
      return;
    }
    setAmount(calculateRubAmount(parseAmountInput(usdtAmount), rate));
  }, [withdrawalRate, withdrawMethod]);

  async function loadWithdrawalRate(force = false) {
    const now = Date.now();
    if (
      !force &&
      withdrawalRate &&
      withdrawalRateExpiresAt(withdrawalRate) > now
    ) {
      return;
    }
    if (rateLoading) return;
    setRateLoading(true);
    try {
      const r = await driverApi.getWithdrawalRate();
      if (r.error) {
        setFormError("не удалось обновить курс");
        return;
      }
      setWithdrawalRate(r.data as WithdrawalRate);
    } catch {
      setFormError("ошибка загрузки курса");
    } finally {
      setRateLoading(false);
    }
  }

  async function createWithdrawal(method: WithdrawalMethodValue) {
    if (submitting !== null) return;
    const amountNum = parseAmountInput(amount);
    const available = parseMoney(balance?.available) ?? 0;
    if (amountNum === null || amountNum < MIN_WITHDRAWAL_RUB) {
      triggerHapticFeedback();
      setFormError(
        `минимальная сумма вывода — ${formatRubNumber(MIN_WITHDRAWAL_RUB)} ₽`,
      );
      return;
    }
    if (amountNum > available) {
      triggerHapticFeedback();
      setFormError("сумма превышает доступный баланс");
      return;
    }
    setFormError(null);
    const normalizedAmount =
      normalizeAmountForSubmit(amount) ?? formatInputAmount(amountNum, 2);
    const wallet =
      method === "crypto_check"
        ? cryptoCheckDestination()
        : MANAGER_DESTINATION;

    setSubmitting(method);
    try {
      const r = await driverApi.createWithdrawal({
        amount: normalizedAmount,
        wallet_address: wallet,
        method,
      });
      if (r.error) {
        setFormError(
          financeErrorMessage(r.error, "ошибка при создании заявки"),
        );
        return;
      }
      const created = r.data as {
        amount_rub?: string;
        payout_amount_usdt?: string | null;
      };
      const summary = {
        amountRub: created.amount_rub ?? normalizedAmount,
        amountUsdt:
          created.payout_amount_usdt ||
          usdtAmount ||
          calculateUsdtAmount(amountNum, rateRub()),
      };
      setAmount("");
      setUsdtAmount("");
      setWithdrawMethod(null);
      if (method === "crypto_check") {
        setManagerRequest(summary);
        setWithdrawSuccess(true);
      } else {
        setManagerRequest(summary);
        setWithdrawSuccess(false);
      }
      try {
        await refreshFinanceData();
      } catch {
        setFormError(
          "заявка создана, но не удалось обновить баланс — откройте экран ещё раз",
        );
      }
    } catch {
      setFormError("ошибка сети");
    } finally {
      setSubmitting(null);
    }
  }

  async function cancelWithdrawal(withdrawalId: number) {
    if (cancellingWithdrawalId !== null) return;
    setCancellingWithdrawalId(withdrawalId);
    setCancellationError(null);
    try {
      const response = await driverApi.cancelWithdrawal(withdrawalId);
      if (response.error) {
        setCancellationError(
          financeErrorMessage(response.error, "не удалось отменить заявку"),
        );
        // Сервер мог отклонить действие из-за параллельного решения админа.
        // Обновляем строку и баланс, сохраняя исходную понятную ошибку.
        try {
          await refreshFinanceData();
        } catch {
          // Ошибка действия важнее вторичной ошибки refresh.
        }
        return;
      }

      setOperations((current) =>
        current.map((operation) =>
          withdrawalIdFromOperation(operation) === withdrawalId
            ? { ...operation, status: "cancelled" }
            : operation,
        ),
      );
      setConfirmingCancellationId(null);
      triggerHapticFeedback();
      try {
        await refreshFinanceData();
      } catch {
        setCancellationError(
          "заявка отменена, но не удалось обновить баланс — повторите загрузку",
        );
      }
    } catch {
      setCancellationError("ошибка сети — попробуйте отменить заявку ещё раз");
    } finally {
      setCancellingWithdrawalId(null);
    }
  }

  function openWithdrawMode() {
    setWithdrawMode(true);
    setWithdrawMethod(null);
    setAmount("");
    setUsdtAmount("");
    setConversionSource("rub");
    setFormError(null);
    setWithdrawSuccess(false);
    setManagerRequest(null);
  }

  function closeWithdrawMode() {
    setWithdrawMode(false);
    setWithdrawMethod(null);
    setAmount("");
    setUsdtAmount("");
    setConversionSource("rub");
    setFormError(null);
    setWithdrawSuccess(false);
    setManagerRequest(null);
  }

  function selectWithdrawMethod(method: WithdrawalMethodValue) {
    setWithdrawMethod(method);
    setAmount("");
    setUsdtAmount("");
    setConversionSource("rub");
    setFormError(null);
    void loadWithdrawalRate();
  }

  function handleRubAmountChange(value: string) {
    setFormError(null);
    setConversionSource("rub");
    setAmount(value);
    const rub = parseAmountInput(value);
    setUsdtAmount(calculateUsdtAmount(rub, rateRub()));
  }

  function handleUsdtAmountChange(value: string) {
    setFormError(null);
    setConversionSource("usdt");
    const nextValue = normalizeUsdtInput(value);
    setUsdtAmount(nextValue);
    const usdt = parseAmountInput(nextValue);
    setAmount(calculateRubAmount(usdt, rateRub()));
  }

  function fillMaxAmount() {
    const maxAmount = normalizeAmountForSubmit(balance?.available);
    const available = parseMoney(maxAmount ?? balance?.available);
    if (available === null || available <= 0) return;
    const nextAmount = maxAmount ?? formatInputAmount(available, 2);
    setFormError(null);
    setConversionSource("rub");
    setAmount(nextAmount);
    setUsdtAmount(calculateUsdtAmount(available, rateRub()));
  }

  function rateRub(): number | null {
    return parseMoney(withdrawalRate?.rate_rub_usdt);
  }

  if (loading) {
    return (
      <div className="tp-screen" data-testid="loading" aria-hidden="true" />
    );
  }

  if (error) {
    return (
      <div className="tp-screen">
        <p className="tp-alert" data-testid="error">
          {error}
        </p>
        <button className="tp-button-primary" onClick={load}>
          повторить
        </button>
      </div>
    );
  }

  if (withdrawMode) {
    const currentRate = rateRub();
    const availableRub = parseMoney(balance?.available) ?? 0;
    const courseText = currentRate
      ? `${formatRubNumber(currentRate)} ₽/USDT`
      : "—";

    if (withdrawSuccess) {
      return (
        <div className="tp-screen tp-withdraw-screen">
          <WithdrawHeader title="вывод средств" onBack={closeWithdrawMode} />
          <div className="tp-withdraw-success">
            <img
              src={duckPremiumStarsGif}
              alt=""
              className="tp-withdraw-success-duck"
              draggable={false}
            />
            <p>ваша заявка успешно создана!</p>
            {managerRequest ? (
              <strong>
                {formatRubAmount(managerRequest.amountRub)} ₽ →{" "}
                {managerRequest.amountUsdt} USDT
              </strong>
            ) : null}
          </div>
          {formError ? <p className="tp-alert">{formError}</p> : null}
        </div>
      );
    }

    if (managerRequest) {
      return (
        <div className="tp-screen tp-withdraw-screen">
          <WithdrawHeader
            title="вывод через менеджера"
            onBack={closeWithdrawMode}
          />
          <div className="tp-withdraw-manager-card">
            <div className="tp-withdraw-manager-row">
              <span>сумма вывода</span>
              <strong>{formatRubAmount(managerRequest.amountRub)} ₽</strong>
            </div>
            <div className="tp-withdraw-divider" />
            {managerRequest.amountUsdt ? (
              <>
                <div className="tp-withdraw-manager-row">
                  <span>примерно к получению</span>
                  <strong>{managerRequest.amountUsdt} USDT</strong>
                </div>
                <div className="tp-withdraw-divider" />
              </>
            ) : null}
          </div>
          <button
            type="button"
            className="tp-button-primary tp-withdraw-manager-button w-full"
            onClick={() => openTelegramLink(MANAGER_LINK)}
          >
            открыть менеджера
          </button>
          {formError ? <p className="tp-alert">{formError}</p> : null}
        </div>
      );
    }

    return (
      <div className="tp-screen tp-withdraw-screen">
        <WithdrawHeader title="вывод средств" onBack={closeWithdrawMode} />

        <div className="tp-withdraw-methods">
          <button
            type="button"
            className="tp-withdraw-method"
            data-active={withdrawMethod === "crypto_check"}
            disabled={submitting !== null}
            onClick={() => selectWithdrawMethod("crypto_check")}
          >
            вывести без комиссии (в Crypto Bot)
          </button>
          <button
            type="button"
            className="tp-withdraw-method"
            data-active={withdrawMethod === "support"}
            disabled={submitting !== null}
            onClick={() => selectWithdrawMethod("support")}
          >
            вывод через менеджера
          </button>
        </div>

        {withdrawMethod ? (
          <div className="tp-withdraw-conversion">
            <div className="tp-withdraw-refresh-row">
              <button
                type="button"
                disabled={rateLoading}
                onClick={() => void loadWithdrawalRate(true)}
              >
                обновить
              </button>
            </div>

            {withdrawalRate?.is_fallback ? (
              <p className="tp-alert" role="alert">
                показан резервный ориентировочный курс; точный курс будет
                рассчитан при выплате
              </p>
            ) : null}

            <div className="tp-withdraw-conversion-card">
              <div className="tp-withdraw-conversion-row">
                <div className="tp-withdraw-currency-block">
                  <span className="tp-withdraw-row-caption">
                    вы отправляете:
                  </span>
                  <div className="tp-withdraw-currency-label">
                    <RubIcon />
                    <span>RUB</span>
                  </div>
                </div>
                <div className="tp-withdraw-amount-column">
                  <div className="tp-withdraw-inline-balance">
                    <span>баланс: {formatRubAmount(availableRub)} ₽</span>
                    <button
                      type="button"
                      className="tp-withdraw-max-button"
                      onClick={fillMaxAmount}
                    >
                      <span
                        className="tp-withdraw-max-separator"
                        aria-hidden="true"
                      >
                        •
                      </span>
                      <span>макс</span>
                    </button>
                  </div>
                  <div className="tp-withdraw-balance-value">
                    <input
                      className="tp-withdraw-amount-input"
                      aria-label="сумма RUB"
                      placeholder="0"
                      value={amount}
                      onChange={(e) => handleRubAmountChange(e.target.value)}
                      inputMode="decimal"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
              <div className="tp-withdraw-divider" />
              <div className="tp-withdraw-conversion-row tp-withdraw-receive-row">
                <div className="tp-withdraw-currency-block">
                  <span className="tp-withdraw-row-caption">вы получаете:</span>
                  <div className="tp-withdraw-currency-label">
                    <UsdtIcon />
                    <span>USDT*</span>
                  </div>
                </div>
                <div className="tp-withdraw-amount-column">
                  <div className="tp-withdraw-balance-value tp-withdraw-prefixed-input">
                    <span
                      className="tp-withdraw-approximation-sign"
                      aria-hidden="true"
                    >
                      ≈
                    </span>
                    <input
                      className="tp-withdraw-amount-input tp-withdraw-prefixed-amount-input"
                      aria-label="сумма USDT"
                      placeholder="0"
                      value={usdtAmount}
                      style={{
                        width: `${Math.min(Math.max(usdtAmount.length, 1), 18)}ch`,
                      }}
                      onChange={(e) => handleUsdtAmountChange(e.target.value)}
                      inputMode="decimal"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
              <div className="tp-withdraw-divider" />
              <div className="tp-withdraw-course tp-withdraw-course--compact">
                курс: {courseText}
              </div>
              <div className="tp-withdraw-course-note">
                * итоговая сумма считается по курсу на момент выплаты
              </div>
            </div>

            {formError ? (
              <p className="tp-withdraw-form-error" data-testid="form-error">
                {formError}
              </p>
            ) : null}

            <button
              type="button"
              className="tp-button-primary tp-withdraw-request-button w-full"
              aria-busy={submitting !== null}
              onClick={() => createWithdrawal(withdrawMethod)}
            >
              <CoinsIcon />
              <span>
                {submitting === withdrawMethod
                  ? "создаём..."
                  : "запросить вывод"}
              </span>
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  if (historyMode) {
    return (
      <div className="tp-screen tp-balance-history-screen">
        <WithdrawHeader
          title="история транзакций"
          onBack={() => setHistoryMode(false)}
        />
        {cancellationError ? (
          <p className="tp-alert" role="alert">
            {cancellationError}
          </p>
        ) : null}
        <OperationsPanel
          operations={operations}
          confirmingCancellationId={confirmingCancellationId}
          cancellingWithdrawalId={cancellingWithdrawalId}
          onAskCancel={(withdrawalId) => {
            setCancellationError(null);
            setConfirmingCancellationId(withdrawalId);
          }}
          onCancelConfirmation={() => setConfirmingCancellationId(null)}
          onConfirmCancel={cancelWithdrawal}
        />
      </div>
    );
  }

  return (
    <div className="tp-screen tp-balance-screen">
      <div className="tp-balance-hero">
        <img
          src={duckMoneyGif}
          alt=""
          className="tp-balance-duck"
          draggable={false}
        />
        <h1 className="tp-driver-stats-title">управление финансами</h1>
      </div>

      {balance && (
        <div className="tp-balance-card">
          <div className="tp-balance-row">
            <div className="tp-balance-row-label">
              <Wallet size={18} strokeWidth={2} />
              <span>основной баланс:</span>
            </div>
            <span className="tp-balance-value" data-testid="balance-available">
              {formatRubAmount(balance.available)} ₽
            </span>
          </div>
          <div className="tp-balance-divider" />
          <div className="tp-balance-row">
            <div className="tp-balance-row-label">
              <Snowflake size={18} strokeWidth={2} />
              <span>замороженный баланс:</span>
            </div>
            <span className="tp-balance-value" data-testid="balance-frozen">
              {formatRubAmount(balance.frozen)} ₽
            </span>
          </div>
          <div className="tp-balance-divider" />
          <div className="tp-balance-row">
            <div className="tp-balance-row-label">
              <Snowflake size={18} strokeWidth={2} />
              <span>в обработке на вывод:</span>
            </div>
            <span
              className="tp-balance-value"
              data-testid="balance-withdrawal-pending"
            >
              {formatRubAmount(balance.withdrawal_pending)} ₽
            </span>
          </div>
          <div className="tp-balance-divider" />
          <button
            type="button"
            className="tp-button-primary tp-balance-withdraw-button"
            onClick={openWithdrawMode}
          >
            <CoinsIcon />
            <span>сделать вывод</span>
          </button>
        </div>
      )}

      <HistoryEntryButton onOpen={() => setHistoryMode(true)} />

      <DriverApiKeyBlock />
    </div>
  );
}

function DriverApiKeyBlock() {
  const [data, setData] = useState<DriverApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void driverApi.getDriverApiKey().then((resp) => {
      if (!cancelled && !resp.error) {
        setData(resp.data as DriverApiKey);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function copyKey() {
    if (!data?.api_key) return;
    try {
      await navigator.clipboard?.writeText(data.api_key);
      setCopied(true);
      triggerHapticFeedback();
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  }

  return (
    <section className="tp-driver-api-key-section">
      <div className="tp-driver-api-key-eyebrow">ДЛЯ ИНТЕГРАЦИИ</div>
      <div className="tp-driver-api-key-card" data-open={open}>
        <button
          type="button"
          className="tp-driver-api-key-head"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <UserSearch aria-hidden="true" />
          <span>API ключ</span>
          <ChevronDown
            className="tp-driver-api-key-chevron"
            aria-hidden="true"
          />
        </button>
        {open ? (
          <div className="tp-driver-api-key-panel">
            {data ? (
              <>
                <div className="tp-driver-api-key-row">
                  <span className="tp-driver-api-key-value">
                    {data.api_key}
                  </span>
                  <button
                    type="button"
                    className="tp-driver-api-key-icon-button"
                    aria-label="скопировать API ключ"
                    onClick={copyKey}
                  >
                    <Copy aria-hidden="true" />
                  </button>
                </div>
                {copied ? (
                  <div className="tp-driver-api-key-copied">скопировано</div>
                ) : null}
              </>
            ) : (
              <div className="tp-driver-api-key-loading">загружаем ключ...</div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function WithdrawHeader({
  title,
  onBack,
}: {
  title: string;
  onBack: () => void;
}) {
  return (
    <div className="tp-withdraw-header">
      <button type="button" aria-label="назад" onClick={onBack}>
        <ArrowLeft size={20} strokeWidth={2.2} />
      </button>
      <h1>{title}</h1>
    </div>
  );
}

function HistoryEntryButton({ onOpen }: { onOpen: () => void }) {
  return (
    <section className="tp-balance-history-card">
      <button
        type="button"
        className="tp-balance-history-head"
        onClick={onOpen}
      >
        <ClockHour4Icon />
        <span>история транзакций</span>
        <ChevronRight
          className="tp-balance-history-chevron"
          aria-hidden="true"
        />
      </button>
    </section>
  );
}

function OperationsPanel({
  operations,
  confirmingCancellationId,
  cancellingWithdrawalId,
  onAskCancel,
  onCancelConfirmation,
  onConfirmCancel,
}: {
  operations: FinanceOperation[];
  confirmingCancellationId: number | null;
  cancellingWithdrawalId: number | null;
  onAskCancel: (withdrawalId: number) => void;
  onCancelConfirmation: () => void;
  onConfirmCancel: (withdrawalId: number) => void;
}) {
  const visibleOperations = operations.filter(isUserVisibleOperation);
  const groups = groupOperationsByDay(visibleOperations);

  return (
    <div className="tp-balance-history-panel tp-balance-history-panel-page">
      {visibleOperations.length === 0 ? (
        <div className="tp-balance-empty-card">
          <p className="tp-balance-empty-title">история пустая</p>
          <img
            src={duckLookingGif}
            alt=""
            className="tp-balance-empty-duck"
            draggable={false}
          />
          <p className="tp-balance-empty-text">
            когда вы начнете совершать транзакции, они появятся здесь
          </p>
        </div>
      ) : (
        <div className="tp-balance-history-groups">
          {groups.map((group) => (
            <div key={group.key} className="tp-balance-history-group">
              <div className="tp-balance-history-date">{group.label}</div>
              <ul className="tp-balance-history-list">
                {group.items.map((op) => (
                  <OperationRow
                    key={op.id}
                    operation={op}
                    confirmingCancellationId={confirmingCancellationId}
                    cancellingWithdrawalId={cancellingWithdrawalId}
                    onAskCancel={onAskCancel}
                    onCancelConfirmation={onCancelConfirmation}
                    onConfirmCancel={onConfirmCancel}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OperationRow({
  operation,
  confirmingCancellationId,
  cancellingWithdrawalId,
  onAskCancel,
  onCancelConfirmation,
  onConfirmCancel,
}: {
  operation: FinanceOperation;
  confirmingCancellationId: number | null;
  cancellingWithdrawalId: number | null;
  onAskCancel: (withdrawalId: number) => void;
  onCancelConfirmation: () => void;
  onConfirmCancel: (withdrawalId: number) => void;
}) {
  const isModeration = operation.status === "pending";
  const withdrawalId = withdrawalIdFromOperation(operation);
  const canCancel = isModeration && withdrawalId !== null;
  const isConfirming = canCancel && confirmingCancellationId === withdrawalId;
  const isCancelling = canCancel && cancellingWithdrawalId === withdrawalId;
  const course = formatOperationCourse(operation);
  return (
    <li className="tp-balance-operation-row">
      <div className="tp-balance-operation-icon-wrap">
        <OperationArrowIcon />
        {isModeration ? (
          <span className="tp-balance-operation-lock" aria-hidden="true">
            <LockBadgeIcon />
          </span>
        ) : null}
      </div>
      <div className="tp-balance-operation-main">
        <div className="tp-balance-operation-title">
          {operationTitle(operation)}
        </div>
        <div className="tp-balance-operation-time">
          {formatOperationDateTime(operation.created_at)}
        </div>
        {canCancel ? (
          <div className="tp-balance-operation-actions">
            {isConfirming ? (
              <>
                <button
                  type="button"
                  disabled={cancellingWithdrawalId !== null}
                  onClick={onCancelConfirmation}
                >
                  не отменять
                </button>
                <button
                  type="button"
                  className="tp-balance-operation-cancel-confirm"
                  disabled={cancellingWithdrawalId !== null}
                  aria-busy={isCancelling}
                  onClick={() => onConfirmCancel(withdrawalId!)}
                >
                  {isCancelling ? "отменяем..." : "да, отменить"}
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={cancellingWithdrawalId !== null}
                onClick={() => onAskCancel(withdrawalId!)}
              >
                отменить заявку
              </button>
            )}
          </div>
        ) : null}
      </div>
      <div className="tp-balance-operation-side">
        <div className="tp-balance-operation-amount">
          {formatOperationAmount(operation)}
        </div>
        {course ? (
          <div className="tp-balance-operation-course">курс {course}</div>
        ) : null}
      </div>
    </li>
  );
}

function withdrawalIdFromOperation(operation: FinanceOperation): number | null {
  if (operation.kind !== "withdrawal") return null;
  const match = /^withdrawal-(\d+)$/.exec(operation.id);
  if (!match) return null;
  const withdrawalId = Number(match[1]);
  return Number.isSafeInteger(withdrawalId) && withdrawalId > 0
    ? withdrawalId
    : null;
}

function isUserVisibleOperation(operation: FinanceOperation): boolean {
  return (
    operation.kind === "withdrawal" || operation.id.startsWith("withdrawal-")
  );
}

function groupOperationsByDay(operations: FinanceOperation[]) {
  const groups = new Map<
    string,
    { key: string; label: string; items: FinanceOperation[] }
  >();
  for (const operation of operations) {
    const date = new Date(operation.created_at);
    const key = Number.isNaN(date.getTime())
      ? operation.created_at.slice(0, 10)
      : date.toISOString().slice(0, 10);
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(operation);
      continue;
    }
    groups.set(key, {
      key,
      label: formatOperationDay(operation.created_at),
      items: [operation],
    });
  }
  return Array.from(groups.values());
}

function operationTitle(operation: FinanceOperation): string {
  if (operation.status === "pending") return "на модерации";
  if (operation.status === "cancelled") return "отменено";
  if (operation.status === "rejected") return "отклонено";
  const mapped = operationKindLabel(operation.kind ?? operation.title);
  if (mapped) return mapped;
  if (
    operation.kind === "withdrawal" ||
    operation.id.startsWith("withdrawal-")
  ) {
    return "вывод средств";
  }
  return operation.title || labelFrom(FINANCE_STATUS_LABELS, operation.status);
}

function operationKindLabel(value: string | null | undefined): string | null {
  switch (value?.toLowerCase()) {
    case "charge":
      return "списание";
    case "freeze":
      return "заморозка";
    case "unfreeze":
      return "зачисление";
    case "refund":
      return "возврат";
    case "platform_fee":
      return "комиссия сервиса";
    case "topup":
      return "пополнение";
    case "payout":
    case "withdrawal":
    case "вывод":
      return "вывод средств";
    case "payout_cancel":
      return "отмена вывода";
    case "adjustment":
      return "изменение баланса";
    case "conversion_settlement":
      return "начисление";
    default:
      return null;
  }
}

function formatOperationDay(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  });
}

function formatOperationDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${formatOperationDay(value)} ${date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function formatOperationAmount(operation: FinanceOperation): string {
  const rub = parseMoney(operation.amount_rub);
  const sign =
    rub === null || isUserVisibleOperation(operation)
      ? ""
      : rub >= 0
        ? "+"
        : "-";
  const rubText =
    rub === null
      ? String(operation.amount_rub || "0")
      : formatRubNumber(Math.abs(rub));
  const usdt = parseMoney(operation.amount_usdt);
  const usdtText =
    usdt !== null ? ` = ${formatInputAmount(Math.abs(usdt), 2)} USDT` : "";
  return `${sign}${rubText} ₽${usdtText}`;
}

function formatRubAmount(value: string | number | null | undefined): string {
  const parsed = parseMoney(value);
  if (parsed === null) return "0.0";
  return formatRubNumber(parsed);
}

function formatRubNumber(value: number): string {
  return formatMoney(truncateToTenths(value), 1, 1);
}

function formatOperationCourse(operation: FinanceOperation): string | null {
  const rub = parseMoney(operation.amount_rub);
  const usdt = parseMoney(operation.amount_usdt);
  if (rub === null || usdt === null || usdt === 0) return null;
  return formatRubNumber(Math.abs(rub) / Math.abs(usdt));
}

function parseMoney(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function parseAmountInput(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined || value === "") return null;
  const normalized = String(value).replace(/\s/g, "").replace(",", ".").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeAmountForSubmit(
  value: string | number | null | undefined,
): string | null {
  if (value === null || value === undefined) return null;
  const normalized = String(value).replace(/\s/g, "").replace(",", ".").trim();
  if (!normalized) return null;
  const match = normalized.match(/^(\d+)(?:\.(\d+))?$/);
  if (!match) return null;
  const whole = match[1].replace(/^0+(?=\d)/, "") || "0";
  const fraction = match[2]?.replace(/0+$/, "") ?? "";
  return fraction ? `${whole}.${fraction}` : whole;
}

function calculateUsdtAmount(
  rub: number | null,
  rateRubUsdt: number | null,
): string {
  if (rub === null || rateRubUsdt === null || rub <= 0 || rateRubUsdt <= 0) {
    return "";
  }
  return formatInputAmount(rub / rateRubUsdt, 2);
}

function calculateRubAmount(
  usdt: number | null,
  rateRubUsdt: number | null,
): string {
  if (usdt === null || rateRubUsdt === null || usdt <= 0 || rateRubUsdt <= 0) {
    return "";
  }
  return formatInputAmount(usdt * rateRubUsdt, 2);
}

function normalizeUsdtInput(value: string): string {
  const normalized = value.replace(/\s/g, "").replace(",", ".");
  if (!normalized) return "";
  const match = normalized.match(/^(\d*)(?:\.(\d*))?/);
  if (!match) return "";
  const whole = match[1] || (normalized.startsWith(".") ? "0" : "");
  const fraction = match[2];
  if (fraction !== undefined) return `${whole}.${fraction.slice(0, 2)}`;
  return whole;
}

function formatInputAmount(
  value: number,
  maximumFractionDigits: number,
): string {
  return new Intl.NumberFormat("en-US", {
    useGrouping: false,
    maximumFractionDigits,
  }).format(value);
}

function formatMoney(
  value: number,
  maximumFractionDigits = 2,
  minimumFractionDigits = 0,
): string {
  return new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits,
    minimumFractionDigits,
  })
    .format(value)
    .replace(/[\s\u00a0\u202f]/g, ".");
}

function truncateToTenths(value: number): number {
  const truncated = Math.trunc(value * 10) / 10;
  return Object.is(truncated, -0) ? 0 : truncated;
}

function RubIcon() {
  return (
    <span className="tp-withdraw-currency-icon" aria-hidden="true">
      ₽
    </span>
  );
}

function UsdtIcon() {
  return (
    <span className="tp-withdraw-currency-icon" aria-hidden="true">
      ₮
    </span>
  );
}

function CoinsIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M9 14c0 1.657 2.686 3 6 3s6 -1.343 6 -3s-2.686 -3 -6 -3s-6 1.343 -6 3" />
      <path d="M9 14v4c0 1.656 2.686 3 6 3s6 -1.344 6 -3v-4" />
      <path d="M3 6c0 1.072 1.144 2.062 3 2.598s4.144 .536 6 0c1.856 -.536 3 -1.526 3 -2.598c0 -1.072 -1.144 -2.062 -3 -2.598s-4.144 -.536 -6 0c-1.856 .536 -3 1.526 -3 2.598" />
      <path d="M3 6v10c0 .888 .772 1.45 2 2" />
      <path d="M3 11c0 .888 .772 1.45 2 2" />
    </svg>
  );
}

function ClockHour4Icon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
      <path d="M12 12l3 2" />
      <path d="M12 7v5" />
    </svg>
  );
}

function OperationArrowIcon() {
  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="32" cy="32" r="24" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M32 18V38"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M24 31L32 39L40 31"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockBadgeIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function cryptoCheckDestination(): string {
  const user = getTelegramUser();
  if (user?.username) return `@${user.username}`;
  if (user?.id) return `telegram:${user.id}`;
  return "crypto_check";
}

function financeErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== "object") return fallback;
  const payload = error as {
    detail?: unknown;
    error?: { code?: unknown; message?: unknown };
  };
  if (payload.error?.code === "initdata_expired") {
    return "сессия истекла — заново откройте Mini App из driver-бота";
  }
  if (typeof payload.error?.message === "string") {
    return payload.error.message;
  }
  if (typeof payload.detail === "string") return payload.detail;
  return fallback;
}

function withdrawalRateExpiresAt(rate: WithdrawalRate): number {
  const explicitExpiry = rate.expires_at
    ? Date.parse(rate.expires_at)
    : Number.NaN;
  if (Number.isFinite(explicitExpiry)) return explicitExpiry;
  const fetchedAt = Date.parse(rate.fetched_at);
  return Number.isFinite(fetchedAt) ? fetchedAt + WITHDRAWAL_RATE_TTL_MS : 0;
}
